import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary, btnSecondary, formatINR, formatDate } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";
import { Wallet, FileUp, Trash2, Plus, ReceiptText, Eye, Printer, Banknote, ListChecks } from "lucide-react";

function viewDoc(id) {
  api.get(`/documents/${id}`).then(async (r) => {
    const resp = await fetch(r.data.data);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
}

function DisbursementsModal({ row, onClose }) {
  const [data, setData] = useState({ rows: [] });
  useEffect(() => { api.get(`/leads/${row.id}/disbursements`).then((r) => setData(r.data)).catch(() => {}); }, [row.id]);
  return (
    <Modal open={true} onClose={onClose} title={`Disbursements · ${row.customer_name}`} size="lg" testId="disbursements-modal">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="text-slate-500">Sanction: <span className="font-mono font-semibold text-slate-800">{formatINR(data.sanction_amount)}</span></div>
          <div className="text-slate-500">Total Disbursed: <span className="font-mono font-semibold text-slate-800">{formatINR(data.disbursed_amount)}</span></div>
        </div>
        {(data.rows || []).map((d, i) => (
          <div key={i} data-testid={`disbursement-row-${i}`} className="flex items-center justify-between px-4 py-3 rounded-lg border border-slate-200 bg-slate-50">
            <div>
              <div className="text-sm font-medium text-slate-800">{d.type === "part" ? `Part ${i + 1}` : "Full"} · {d.no || "—"}</div>
              <div className="text-xs text-slate-400">{formatDate(d.at)}{d.by ? ` · ${d.by}` : ""}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm font-semibold text-slate-900">{formatINR(d.amount)}</span>
              {d.proof_doc_id && <button data-testid={`disb-proof-${i}`} onClick={() => viewDoc(d.proof_doc_id)} className="text-xs text-emerald-600 hover:underline">Proof</button>}
            </div>
          </div>
        ))}
        {(data.rows || []).length === 0 && <div className="py-10 text-center text-slate-400 text-sm">No disbursements recorded yet.</div>}
      </div>
    </Modal>
  );
}

function uploadProof(entityId, file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const r = await api.post("/documents", { entity_type: "lead", entity_id: entityId, doc_type: "Disbursement Proof", file_name: file.name, data: reader.result });
        resolve({ id: r.data.id, name: file.name });
      } catch (err) { reject(err); }
    };
    reader.readAsDataURL(file);
  });
}

function DisburseModal({ row, onClose, onDone }) {
  const [disbType, setDisbType] = useState("full");
  const [amount, setAmount] = useState(String(row.pending > 0 ? row.pending : row.sanction_amount || ""));
  const [disbNo, setDisbNo] = useState("");
  const [proof, setProof] = useState({ id: "", name: "" });
  const [parts, setParts] = useState([{ no: "", amount: "", proof_doc_id: "", proof_name: "" }]);
  const [uploading, setUploading] = useState(-2);
  const alreadyPart = row.disbursement_type === "part" && (row.disbursement_parts || []).length > 0;

  const setRow = (i, k, v) => { const n = [...parts]; n[i] = { ...n[i], [k]: v }; setParts(n); };
  const addRow = () => setParts([...parts, { no: "", amount: "", proof_doc_id: "", proof_name: "" }]);
  const removeRow = (i) => setParts(parts.length > 1 ? parts.filter((_, x) => x !== i) : parts);
  const partsTotal = parts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  const pickFull = async (e) => {
    const f = e.target.files[0]; e.target.value = ""; if (!f) return;
    setUploading(-1);
    try { const r = await uploadProof(row.id, f); setProof(r); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setUploading(-2);
  };
  const pickPart = async (i, e) => {
    const f = e.target.files[0]; e.target.value = ""; if (!f) return;
    setUploading(i);
    try { const r = await uploadProof(row.id, f); const n = [...parts]; n[i] = { ...n[i], proof_doc_id: r.id, proof_name: r.name }; setParts(n); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setUploading(-2);
  };

  const cleanParts = () => {
    const kept = parts.filter((p) => p.no.trim() || p.amount || p.proof_doc_id);
    for (const p of kept) {
      if (!p.no.trim()) { toast.error("Each part needs a Disbursement No"); return null; }
      if (!(parseFloat(p.amount) > 0)) { toast.error("Each part needs an amount > 0"); return null; }
      if (!p.proof_doc_id) { toast.error("Upload proof for each part"); return null; }
    }
    if (!kept.length) { toast.error("Add at least one part"); return null; }
    return kept.map((p) => ({ no: p.no.trim(), amount: parseFloat(p.amount), proof_doc_id: p.proof_doc_id, proof_name: p.proof_name }));
  };

  const submit = async () => {
    try {
      if (alreadyPart) {
        const cp = cleanParts(); if (!cp) return;
        await api.patch(`/leads/${row.id}/disbursement-parts`, { parts: cp });
      } else if (disbType === "part") {
        const cp = cleanParts(); if (!cp) return;
        await api.patch(`/leads/${row.id}/status`, { status: "Disbursed", disbursement_type: "part", disbursement_parts: cp });
      } else {
        if (!disbNo.trim()) { toast.error("Disbursement No is required"); return; }
        if (!proof.id) { toast.error("Upload proof of disbursement"); return; }
        await api.patch(`/leads/${row.id}/status`, { status: "Disbursed", disbursement_type: "full", disbursed_amount: parseFloat(amount) || 0, disbursement_no: disbNo.trim(), disbursement_proof_doc_id: proof.id, disbursement_proof_name: proof.name });
      }
      toast.success("Disbursement recorded");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Modal open={true} onClose={onClose} title={`Disburse · ${row.customer_name}`} testId="disburse-modal">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="text-slate-500">Sanction: <span className="font-mono font-semibold text-slate-800">{formatINR(row.sanction_amount)}</span></div>
          <div className="text-slate-500">Disbursed: <span className="font-mono font-semibold text-slate-800">{formatINR(row.disbursed_amount)}</span></div>
        </div>
        {alreadyPart ? (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">This lead is part-disbursed. Add more tranches below.</div>
        ) : (
          <div>
            <label className={labelCls}>Disbursement Type</label>
            <div className="flex gap-3">
              {[["full", "Full"], ["part", "Part"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`fin-disb-type-${v}`} onClick={() => setDisbType(v)}
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${disbType === v ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
              ))}
            </div>
          </div>
        )}
        {!alreadyPart && disbType === "full" ? (
          <>
            <div><label className={labelCls}>Disbursed Amount (₹)</label><input data-testid="fin-disb-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Disbursement No *</label><input data-testid="fin-disb-no" value={disbNo} onChange={(e) => setDisbNo(e.target.value)} className={inputCls} placeholder="Disbursal reference no" /></div>
            <div>
              <label className={labelCls}>Proof of Disbursement *</label>
              <label className={`${btnSecondary} cursor-pointer`} data-testid="fin-disb-proof-label">
                <FileUp size={15} /> {uploading === -1 ? "Uploading…" : (proof.name ? "Replace" : "Upload")}
                <input type="file" className="hidden" data-testid="fin-disb-proof-input" onChange={pickFull} />
              </label>
              {proof.name && <span className="text-xs text-emerald-600 ml-3">{proof.name}</span>}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            {parts.map((p, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end p-3 rounded-lg border border-slate-200 bg-slate-50">
                <div className="sm:col-span-4"><label className={labelCls}>Part {i + 1} · Disb No *</label><input data-testid={`fin-part-no-${i}`} value={p.no} onChange={(e) => setRow(i, "no", e.target.value)} className={inputCls} /></div>
                <div className="sm:col-span-3"><label className={labelCls}>Amount (₹) *</label><input data-testid={`fin-part-amount-${i}`} type="number" value={p.amount} onChange={(e) => setRow(i, "amount", e.target.value)} className={inputCls} /></div>
                <div className="sm:col-span-4">
                  <label className={labelCls}>Proof *</label>
                  <label className={`${btnSecondary} cursor-pointer w-full justify-center`} data-testid={`fin-part-proof-label-${i}`}><FileUp size={15} /> {uploading === i ? "Uploading…" : (p.proof_name ? "Replace" : "Upload")}<input type="file" className="hidden" data-testid={`fin-part-proof-input-${i}`} onChange={(e) => pickPart(i, e)} /></label>
                  {p.proof_name && <div className="text-xs text-emerald-600 mt-1 truncate">{p.proof_name}</div>}
                </div>
                <div className="sm:col-span-1 flex justify-end"><button type="button" onClick={() => removeRow(i)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button></div>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <button type="button" data-testid="fin-add-part" onClick={addRow} className={btnSecondary}><Plus size={15} /> Add Part</button>
              <div className="text-sm text-slate-600">Total: <span className="font-mono font-semibold text-slate-900">{formatINR(partsTotal)}</span></div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
          <button data-testid="fin-disburse-submit" onClick={submit} className={btnPrimary}><Banknote size={16} /> Record Disbursement</button>
        </div>
      </div>
    </Modal>
  );
}

function BillModal({ row, items, onClose, onDone }) {
  const active = items.filter((i) => i.active !== false);
  const [billDate, setBillDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ item_id: "", name: "", qty: 1, rate: "", tax_percent: 0 }]);

  const pickItem = (i, id) => {
    const it = active.find((x) => x.id === id);
    const n = [...lines];
    n[i] = it ? { item_id: it.id, name: it.name, qty: n[i].qty || 1, rate: it.default_rate, tax_percent: it.tax_percent || 0 } : { ...n[i], item_id: "" };
    setLines(n);
  };
  const setLine = (i, k, v) => { const n = [...lines]; n[i] = { ...n[i], [k]: v }; setLines(n); };
  const addLine = () => setLines([...lines, { item_id: "", name: "", qty: 1, rate: "", tax_percent: 0 }]);
  const removeLine = (i) => setLines(lines.length > 1 ? lines.filter((_, x) => x !== i) : lines);

  const calc = lines.map((l) => {
    const sub = (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0);
    const tax = sub * (parseFloat(l.tax_percent) || 0) / 100;
    return { sub, tax, total: sub + tax };
  });
  const subtotal = calc.reduce((s, c) => s + c.sub, 0);
  const taxTotal = calc.reduce((s, c) => s + c.tax, 0);
  const grand = subtotal + taxTotal;

  const submit = async () => {
    const payloadItems = lines.filter((l) => (l.name || "").trim()).map((l) => ({ item_id: l.item_id, name: l.name, qty: parseFloat(l.qty) || 0, rate: parseFloat(l.rate) || 0, tax_percent: parseFloat(l.tax_percent) || 0 }));
    if (!payloadItems.length) { toast.error("Add at least one bill item"); return; }
    try {
      await api.post(`/leads/${row.id}/bills`, { bill_date: billDate, notes, items: payloadItems });
      toast.success("Bill generated");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Modal open={true} onClose={onClose} title={`Generate Bill · ${row.customer_name}`} size="lg" testId="bill-modal">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Bill Date</label><input data-testid="bill-date" type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} className={inputCls} /></div>
          <div className="text-sm text-slate-500 flex items-end pb-2">Pending after existing bills: <span className="font-mono font-semibold text-slate-800 ml-1">{formatINR(row.pending)}</span></div>
        </div>
        {active.length === 0 && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">No bill items defined yet. An admin can add them under Bill Items — you can still type a custom line name below.</div>}
        <div className="space-y-3">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`bill-line-${i}`}>
              <div className="sm:col-span-4">
                <label className={labelCls}>Item</label>
                <select data-testid={`bill-line-item-${i}`} value={l.item_id} onChange={(e) => pickItem(i, e.target.value)} className={inputCls}>
                  <option value="">— Select / custom —</option>
                  {active.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                </select>
                {!l.item_id && <input data-testid={`bill-line-name-${i}`} value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} className={`${inputCls} mt-1`} placeholder="Custom item name" />}
              </div>
              <div className="sm:col-span-2"><label className={labelCls}>Qty</label><input data-testid={`bill-line-qty-${i}`} type="number" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Rate (₹)</label><input data-testid={`bill-line-rate-${i}`} type="number" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Tax %</label><input data-testid={`bill-line-tax-${i}`} type="number" value={l.tax_percent} onChange={(e) => setLine(i, "tax_percent", e.target.value)} className={inputCls} /></div>
              <div className="sm:col-span-1 text-right text-sm font-mono text-slate-700 pb-2">{formatINR(calc[i].total)}</div>
              <div className="sm:col-span-1 flex justify-end"><button type="button" onClick={() => removeLine(i)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button></div>
            </div>
          ))}
          <button type="button" data-testid="bill-add-line" onClick={addLine} className={btnSecondary}><Plus size={15} /> Add Line</button>
        </div>
        <div><label className={labelCls}>Notes</label><input data-testid="bill-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder="Optional remark on the bill" /></div>
        <div className="flex flex-col items-end gap-1 text-sm border-t border-slate-100 pt-3">
          <div className="text-slate-500">Subtotal: <span className="font-mono font-semibold text-slate-800">{formatINR(subtotal)}</span></div>
          <div className="text-slate-500">Tax: <span className="font-mono font-semibold text-slate-800">{formatINR(taxTotal)}</span></div>
          <div className="text-slate-900 font-semibold">Grand Total: <span className="font-mono" data-testid="bill-grand-total">{formatINR(grand)}</span></div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
          <button data-testid="bill-submit" onClick={submit} className={btnPrimary}><ReceiptText size={16} /> Create Bill</button>
        </div>
      </div>
    </Modal>
  );
}

function printBill(b) {
  const rows = (b.items || []).map((it) => `<tr><td>${it.name}</td><td style="text-align:right">${it.qty}</td><td style="text-align:right">₹${(it.rate || 0).toLocaleString("en-IN")}</td><td style="text-align:right">${it.tax_percent || 0}%</td><td style="text-align:right">₹${(it.line_total || 0).toLocaleString("en-IN")}</td></tr>`).join("");
  const html = `<html><head><title>${b.bill_no}</title><style>body{font-family:Arial,sans-serif;color:#0f172a;padding:32px;max-width:720px;margin:auto}h1{font-size:22px;margin:0}table{width:100%;border-collapse:collapse;margin-top:16px}th,td{padding:8px;border-bottom:1px solid #e2e8f0;font-size:13px}th{text-align:left;background:#f8fafc;text-transform:uppercase;font-size:11px;letter-spacing:.05em;color:#64748b}.tot{text-align:right;margin-top:16px;font-size:14px}.grand{font-size:18px;font-weight:700;margin-top:6px}</style></head><body>
  <div style="display:flex;justify-content:space-between;align-items:flex-start"><div><h1>Arjun Fintech LLP</h1><div style="color:#64748b;font-size:12px">Bill / Invoice</div></div><div style="text-align:right"><div style="font-weight:700">${b.bill_no}</div><div style="color:#64748b;font-size:12px">${b.bill_date || ""}</div></div></div>
  <div style="margin-top:20px;font-size:13px"><div><strong>Bill To:</strong> ${b.customer_name}</div>${b.customer_phone ? `<div>${b.customer_phone}</div>` : ""}${b.customer_email ? `<div>${b.customer_email}</div>` : ""}<div style="color:#64748b">${b.loan_type}${b.lender_name ? " · " + b.lender_name : ""}</div></div>
  <table><thead><tr><th>Item</th><th style="text-align:right">Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">Tax</th><th style="text-align:right">Amount</th></tr></thead><tbody>${rows}</tbody></table>
  <div class="tot">Subtotal: ₹${(b.subtotal || 0).toLocaleString("en-IN")}</div><div class="tot">Tax: ₹${(b.tax_total || 0).toLocaleString("en-IN")}</div><div class="tot grand">Grand Total: ₹${(b.grand_total || 0).toLocaleString("en-IN")}</div>
  ${b.notes ? `<div style="margin-top:16px;color:#64748b;font-size:12px">Notes: ${b.notes}</div>` : ""}
  </body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
}

function ViewBillsModal({ row, onClose, onChanged }) {
  const [bills, setBills] = useState([]);
  const load = useCallback(() => api.get(`/leads/${row.id}/bills`).then((r) => setBills(r.data)).catch(() => {}), [row.id]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!window.confirm("Delete this bill? Pending will increase back.")) return;
    try { await api.delete(`/bills/${id}`); toast.success("Bill deleted"); load(); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <Modal open={true} onClose={onClose} title={`Bills · ${row.customer_name}`} size="lg" testId="view-bills-modal">
      <div className="space-y-3">
        {bills.map((b) => (
          <div key={b.id} data-testid={`bill-card-${b.id}`} className="p-4 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div><div className="font-semibold text-slate-900">{b.bill_no}</div><div className="text-xs text-slate-400">{b.bill_date} · by {b.created_by}</div></div>
              <div className="flex items-center gap-3">
                <span className="font-mono font-semibold text-slate-900">{formatINR(b.grand_total)}</span>
                <button data-testid={`print-bill-${b.id}`} onClick={() => printBill(b)} className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Printer size={15} /></button>
                <button data-testid={`delete-bill-${b.id}`} onClick={() => del(b.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-500">{(b.items || []).map((it) => `${it.name} × ${it.qty}`).join(", ")}</div>
          </div>
        ))}
        {bills.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">No bills for this lead yet.</div>}
      </div>
    </Modal>
  );
}

export default function Finance() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [disburse, setDisburse] = useState(null);
  const [bill, setBill] = useState(null);
  const [viewBills, setViewBills] = useState(null);
  const [viewDisb, setViewDisb] = useState(null);

  const load = useCallback(() => api.get("/finance/leads").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/bill-items").then((r) => setItems(r.data)).catch(() => {}); }, [load]);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><Wallet size={26} className="text-emerald-600" /> Finance</h1>
        <p className="text-sm text-slate-500 mt-1">Sanctioned leads — record disbursements and generate bills. Pending = Sanction − Disbursed − Billed.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[960px]">
          <thead><tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Loan Type</th><th className="text-left px-4 py-3">Lender</th>
            <th className="text-right px-4 py-3">Sanction</th><th className="text-right px-4 py-3">Disbursed</th><th className="text-right px-4 py-3">Billed</th><th className="text-right px-4 py-3">Pending</th>
            <th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`finance-row-${r.id}`} className="border-b border-slate-100 text-sm text-slate-700">
                <td className="px-4 py-3 font-medium text-slate-900">{r.customer_name}</td>
                <td className="px-4 py-3">{r.loan_type}</td>
                <td className="px-4 py-3">{r.lender_name || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{formatINR(r.sanction_amount)}</td>
                <td className="px-4 py-3 text-right font-mono">{formatINR(r.disbursed_amount)}</td>
                <td className="px-4 py-3 text-right font-mono">{formatINR(r.billed)}</td>
                <td className={`px-4 py-3 text-right font-mono font-semibold ${r.pending > 0 ? "text-amber-600" : "text-emerald-600"}`}>{formatINR(r.pending)}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-slate-100 text-slate-600 border-slate-200">{r.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button data-testid={`disburse-btn-${r.id}`} onClick={() => setDisburse(r)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1"><Banknote size={13} /> Disburse</button>
                    <button data-testid={`view-disb-btn-${r.id}`} onClick={() => setViewDisb(r)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1"><ListChecks size={13} /> Disb.</button>
                    <button data-testid={`gen-bill-btn-${r.id}`} onClick={() => setBill(r)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1"><Plus size={13} /> Bill</button>
                    <button data-testid={`view-bills-btn-${r.id}`} onClick={() => setViewBills(r)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1"><Eye size={13} /> {r.bill_count} Bill(s)</button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">No sanctioned leads yet. Leads appear here once marked Approved with a sanction amount.</td></tr>}
          </tbody>
        </table>
      </div>

      {disburse && <DisburseModal row={disburse} onClose={() => setDisburse(null)} onDone={() => { setDisburse(null); load(); }} />}
      {viewDisb && <DisbursementsModal row={viewDisb} onClose={() => setViewDisb(null)} />}
      {bill && <BillModal row={bill} items={items} onClose={() => setBill(null)} onDone={() => { setBill(null); load(); }} />}
      {viewBills && <ViewBillsModal row={viewBills} onClose={() => setViewBills(null)} onChanged={load} />}
    </div>
  );
}
